// frontend/src/pages/ProductsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import useApi from '../hooks/useApi';
import { 
    Plus, Search, Edit, Trash2, X, Save, Loader2, Package, 
    Layers, Truck, AlertCircle, Info, Hash, DollarSign,
    ChevronDown, ChevronUp, Boxes, Clock, User
} from 'lucide-react';

const ProductsPage = ({ currentUser, onDataChange }) => {
    const { apiFetch, permissions } = useApi();
    const [products, setProducts] = useState([]);
    const [classifications, setClassifications] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('geral');
    const [expandedRows, setExpandedRows] = useState(new Set());
    
    // Estados do Formulário
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        classification_id: '',
        is_fractioned: false,
        is_fixed_asset: false, // NOVO: Ativo Imobilizado
        content_quantity: '',
        created_at: '', // Visual apenas
        updated_at: '', // Visual apenas
        variations: [{ code_ean: '', code_reduced: '', description: '', cost_price: 0, sale_price: 0, min_stock: 0 }],
        packagings: []
    });

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [supplierSearchIndices, setSupplierSearchIndices] = useState({});

    // Estado para o Histórico (Auditoria)
    const [showHistory, setShowHistory] = useState(false);
    const [auditLogs, setAuditLogs] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    const canCreate = permissions?.products_create || currentUser?.is_super_user;
    const canEdit = permissions?.products_edit || currentUser?.is_super_user;
    const canDelete = permissions?.products_delete || currentUser?.is_super_user;

    // --- FUNÇÃO DE FORMATAÇÃO DE MOEDA (CORRIGIDA PARA ACEITAR 0) ---
    const formatCurrencyInput = (value) => {
        if (value === undefined || value === null || value === "") return "0,00";
        let stringValue = typeof value === 'number' 
            ? Math.round(value * 100).toString() 
            : value.toString().replace(/\D/g, "");
        if (stringValue === "") return "0,00";
        return (Number(stringValue) / 100).toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [prodData, classData, suppData] = await Promise.all([
                apiFetch('/products'),
                apiFetch('/product-classifications'),
                apiFetch('/general-clients')
            ]);
            setProducts(prodData || []);
            setClassifications(classData || []);
            setSuppliers(suppData.filter(s => s.classification === 'Fornecedor' || s.classification === 'Ambos' || s.classification === 'Fornecedor' ) || []);
        } catch (err) {
            console.error("Erro:", err);
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const fetchAuditHistory = async (id) => {
        setLoadingHistory(true);
        try {
            const logs = await apiFetch(`/products/${id}/history`);
            setAuditLogs(logs || []);
            setShowHistory(true);
        } catch (err) {
            alert("Erro ao carregar histórico: " + err.message);
        } finally {
            setLoadingHistory(false);
        }
    };

    const toggleRow = (id) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(id)) newExpanded.delete(id);
        else newExpanded.add(id);
        setExpandedRows(newExpanded);
    };

    const handleOpenModal = async (product = null) => {
        setError('');
        setActiveTab('geral');
        setSupplierSearchIndices({});
        setShowHistory(false);
        if (product) {
            setEditingId(product.id);
            try {
                const fullProduct = await apiFetch(`/products/${product.id}`);
                setFormData({
                    name: fullProduct.name || '',
                    classification_id: fullProduct.classification_id,
                    is_fractioned: fullProduct.is_fractioned === 1,
                    is_fixed_asset: fullProduct.is_fixed_asset === 1,
                    content_quantity: fullProduct.content_quantity,
                    created_at: fullProduct.created_at,
                    updated_at: fullProduct.updated_at,
                    variations: fullProduct.variations,
                    packagings: fullProduct.packagings || []
                });
            } catch (err) { alert("Erro ao carregar produto"); }
        } else {
            setEditingId(null);
            setFormData({
                name: '', classification_id: '', is_fractioned: false, is_fixed_asset: false, content_quantity: '',
                created_at: '', updated_at: '',
                variations: [{ code_ean: '', code_reduced: '', description: '', cost_price: 0, sale_price: 0, min_stock: 0 }],
                packagings: []
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingId(null);
        setShowHistory(false);
    };

    const addVariation = () => {
        setFormData(prev => ({
            ...prev,
            variations: [...prev.variations, { code_ean: '', code_reduced: '', description: '', cost_price: 0, sale_price: 0, min_stock: 0 }]
        }));
    };

    const handleVariationChange = (index, field, value) => {
        const newVars = [...formData.variations];
        if (field === 'cost_price' || field === 'sale_price') {
            const cleanValue = value.replace(/\D/g, "");
            newVars[index][field] = cleanValue === "" ? 0 : Number(cleanValue) / 100;
        } else {
            newVars[index][field] = value;
        }
        setFormData({ ...formData, variations: newVars });
    };

    const addPackaging = () => {
        setFormData(prev => ({
            ...prev,
            packagings: [...prev.packagings, { supplier_id: '', packaging_type: 'un', conversion_factor: 1 }]
        }));
    };

    const handlePackagingChange = (index, field, value) => {
        const newPacks = [...formData.packagings];
        newPacks[index][field] = value;
        setFormData({ ...formData, packagings: newPacks });
    };

    const filterSuppliers = (query) => {
        if (!query) return [];
        const lowQuery = query.toLowerCase();
        return suppliers.filter(s => 
            s.name.toLowerCase().includes(lowQuery) || 
            (s.document_id && s.document_id.includes(lowQuery))
        ).slice(0, 5);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name) return setError('O nome do produto é obrigatório.');
        if (!formData.classification_id) return setError('Selecione uma classificação.');
        
        setSubmitting(true);
        try {
            await apiFetch('/products', { method: 'POST', body: { ...formData, id: editingId } });
            fetchData();
            handleCloseModal();
            if (onDataChange) onDataChange();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    // --- LÓGICA DE BUSCA ATUALIZADA PARA INCLUIR CÓDIGO REDUZIDO ---
    const filteredProducts = products.filter(p => {
        const lowerSearch = searchTerm.toLowerCase();
        return (
            p.name.toLowerCase().includes(lowerSearch) ||
            p.variations.some(v => 
                v.description.toLowerCase().includes(lowerSearch) || 
                (v.code_ean && v.code_ean.includes(searchTerm)) ||
                (v.code_reduced && String(v.code_reduced).toLowerCase().includes(lowerSearch))
            )
        );
    });

    return (
        <div className="p-4 md:p-6 max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2"><Boxes className="text-blue-600" /> Cadastro de Produtos</h1>
                <button onClick={() => handleOpenModal()} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-semibold flex items-center gap-2 shadow-sm transition-all">
                    <Plus size={20} /> Novo Produto
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 border-b bg-gray-50 flex items-center gap-3">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                        <input 
                            type="text" placeholder="Buscar por produto, variação, EAN ou Reduzido..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-gray-600 font-bold uppercase text-[10px] tracking-wider">
                            <tr>
                                <th className="px-6 py-4 w-10"></th>
                                <th className="px-6 py-4">Produto</th>
                                <th className="px-6 py-4">Classificação</th>
                                <th className="px-6 py-4 text-center">Conteúdo</th>
                                <th className="px-6 py-4 text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-500"><Loader2 className="animate-spin inline mr-2" /> Carregando...</td></tr>
                            ) : filteredProducts.length === 0 ? (
                                <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-500 italic">Nenhum produto encontrado.</td></tr>
                            ) : (
                                filteredProducts.map(product => {
                                    const isExpanded = expandedRows.has(product.id);
                                    return (
                                        <React.Fragment key={product.id}>
                                            <tr className="hover:bg-gray-50 transition-colors align-middle group">
                                                <td className="px-6 py-4">
                                                    <button onClick={() => toggleRow(product.id)} className="p-1 hover:bg-gray-200 rounded text-gray-500">
                                                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                                    </button>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="font-bold text-blue-700 uppercase text-sm">{product.name}</div>
                                                    <div className="text-[10px] text-gray-400">{product.variations.length} variações</div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-[10px] font-bold uppercase">{product.classification_name}</span>
                                                </td>
                                                <td className="px-6 py-4 text-center font-medium text-gray-700">{product.content_quantity || '-'}</td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex justify-end gap-2">
                                                        {canEdit && <button onClick={() => handleOpenModal(product)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"><Edit size={18} /></button>}
                                                        {canDelete && <button onClick={() => { if(window.confirm("Excluir?")) apiFetch(`/products/${product.id}`, {method:'DELETE'}).then(fetchData)}} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={18} /></button>}
                                                    </div>
                                                </td>
                                            </tr>
                                            {isExpanded && (
                                                <tr className="bg-blue-50/30 animate-in fade-in slide-in-from-top-1 duration-200">
                                                    <td colSpan="5" className="px-16 py-4">
                                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                            {product.variations.map((v, idx) => (
                                                                <div key={idx} className="bg-white p-3 rounded-lg border border-blue-100 shadow-sm">
                                                                    <div className="font-bold text-gray-800 border-b border-gray-100 pb-1 mb-2 text-xs uppercase">{v.description}</div>
                                                                    <div className="space-y-1 text-[11px]">
                                                                        <div className="flex justify-between"><span className="text-gray-400">EAN:</span> <span className="font-mono">{v.code_ean || '-'}</span></div>
                                                                        {/* EXIBIÇÃO DO CÓDIGO REDUZIDO NO CARD */}
                                                                        <div className="flex justify-between"><span className="text-gray-400">RED:</span> <span className="font-mono">{v.code_reduced || '-'}</span></div>
                                                                        <div className="flex justify-between font-bold text-gray-600"><span className="text-gray-400 font-normal italic">CUSTO:</span> R$ {v.cost_price.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</div>
                                                                        <div className="flex justify-between font-bold text-green-700"><span className="text-gray-400 font-normal">VENDA:</span> R$ {v.sale_price.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</div>
                                                                        <div className="flex justify-between font-bold text-orange-600 pt-1 border-t border-dotted mt-1">
                                                                            <span className="text-gray-400 font-normal">ESTOQUE MÍN:</span> {v.min_stock}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* MODAL PRINCIPAL */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
                            <div className="flex items-center gap-3">
                                <h3 className="font-bold text-gray-800 text-lg flex items-center gap-2">
                                    <Package className="text-blue-600" />
                                    {editingId ? 'Editar Produto' : 'Novo Produto'}
                                </h3>
                                {editingId && (
                                    <button 
                                        type="button"
                                        onClick={() => fetchAuditHistory(editingId)}
                                        className="p-1.5 text-blue-500 hover:bg-blue-100 rounded-full transition-colors"
                                        title="Histórico de alterações"
                                    >
                                        <Info size={20} />
                                    </button>
                                )}
                            </div>
                            <button onClick={handleCloseModal} className="p-1 hover:bg-gray-200 rounded-full transition-colors"><X size={24} /></button>
                        </div>

                        <div className="flex border-b bg-white px-6">
                            <button onClick={() => setActiveTab('geral')} className={`px-4 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'geral' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Geral</button>
                            <button onClick={() => setActiveTab('variacoes')} className={`px-4 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'variacoes' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Variações / Estoque</button>
                            <button onClick={() => setActiveTab('fornecedores')} className={`px-4 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'fornecedores' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Embalagens x Fornecedor</button>
                        </div>
                        
                        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden relative">
                            {/* OVERLAY DE HISTÓRICO */}
                            {showHistory && (
                                <div className="absolute inset-0 z-40 bg-white flex flex-col animate-in slide-in-from-right duration-300">
                                    <div className="px-6 py-4 border-b bg-gray-50 flex justify-between items-center">
                                        <div className="flex items-center gap-2 font-bold text-gray-700">
                                            <Clock size={18} /> Histórico de Alterações
                                        </div>
                                        <button onClick={() => setShowHistory(false)} className="text-gray-500 hover:text-gray-800 font-bold flex items-center gap-1">
                                            <X size={18} /> Fechar
                                        </button>
                                    </div>
                                    <div className="flex-1 overflow-y-auto p-6">
                                        {loadingHistory ? (
                                            <div className="flex justify-center p-10"><Loader2 className="animate-spin text-blue-600" /></div>
                                        ) : auditLogs.length === 0 ? (
                                            <p className="text-center text-gray-400 italic">Nenhum histórico encontrado para este produto.</p>
                                        ) : (
                                            <div className="space-y-4">
                                                {auditLogs.map((log, i) => (
                                                    <div key={i} className="border-l-4 border-blue-500 bg-gray-50 p-3 rounded-r-lg shadow-sm">
                                                        <div className="flex justify-between items-start mb-1">
                                                            <span className="text-xs font-bold text-blue-700 uppercase">
                                                                {log.action === 'CREATE' ? 'CRIAÇÃO' : 
                                                                 log.action === 'UPDATE' ? 'ALTERAÇÃO' : log.action}
                                                            </span>
                                                            <span className="text-[10px] text-gray-400">
                                                                {log.timestamp && log.timestamp.includes('-') 
                                                                    ? log.timestamp.split(' ')[0].split('-').reverse().join('/') + ', ' + log.timestamp.split(' ')[1]
                                                                    : new Date(log.timestamp).toLocaleString('pt-BR')}
                                                            </span>
                                                        </div>
                                                        <div className="text-sm text-gray-700">
                                                            {log.field_name === 'INITIAL_INSERT' ? (
                                                                <span>Produto cadastrado no sistema.</span>
                                                            ) : (
                                                                <span>{log.new_value}</span>
                                                            )}
                                                        </div>
                                                        <div className="mt-2 flex items-center gap-1 text-[10px] text-gray-500 font-medium">
                                                            <User size={12} /> Alterado por: {log.username || 'Sistema'}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                                {error && <div className="p-3 bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg flex items-center gap-2"><AlertCircle size={18} /> {error}</div>}

                                {activeTab === 'geral' && (
                                    <div className="space-y-6 animate-in fade-in duration-200">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="md:col-span-2">
                                                <label className="block text-sm font-bold text-gray-700 mb-1">Nome do Produto Transformador <span className="text-red-500">*</span></label>
                                                <input 
                                                    type="text" required
                                                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none uppercase"
                                                    value={formData.name}
                                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                    placeholder="Ex: GELATINA, DETERGENTE..."
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-bold text-gray-700 mb-1">Classificação <span className="text-red-500">*</span></label>
                                                <select 
                                                    required
                                                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                                    value={formData.classification_id}
                                                    onChange={(e) => setFormData({ ...formData, classification_id: e.target.value })}
                                                >
                                                    <option value="">Selecione...</option>
                                                    {classifications.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-bold text-gray-700 mb-1">Conteúdo Unitário (Ex: 100g, 1L)</label>
                                                <input 
                                                    type="text"
                                                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                                    value={formData.content_quantity}
                                                    onChange={(e) => setFormData({ ...formData, content_quantity: e.target.value })}
                                                    placeholder="Ex: 500ml"
                                                />
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap items-center gap-6 pt-2">
                                            <div className="flex items-center gap-2">
                                                <input 
                                                    type="checkbox" id="is_fractioned"
                                                    className="w-5 h-5 text-blue-600 rounded"
                                                    checked={formData.is_fractioned}
                                                    onChange={(e) => setFormData({ ...formData, is_fractioned: e.target.checked })}
                                                />
                                                <label htmlFor="is_fractioned" className="text-sm font-bold text-gray-700 cursor-pointer">Produto Fracionado</label>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <input 
                                                    type="checkbox" id="is_fixed_asset"
                                                    className="w-5 h-5 text-blue-600 rounded"
                                                    checked={formData.is_fixed_asset}
                                                    onChange={(e) => setFormData({ ...formData, is_fixed_asset: e.target.checked })}
                                                />
                                                <label htmlFor="is_fixed_asset" className="text-sm font-bold text-gray-700 cursor-pointer">Ativo Imobilizado</label>
                                            </div>
                                        </div>

                                        {/* CAMPOS VISUAIS DE DATA COM CORREÇÃO DE EXIBIÇÃO */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                                            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider">Data de Criação</label>
                                                <div className="text-sm font-semibold text-gray-600 mt-1 flex items-center gap-2">
                                                    <Clock size={14} /> 
                                                    {formData.created_at ? (
                                                        formData.created_at.includes('-') 
                                                            ? formData.created_at.split(' ')[0].split('-').reverse().join('/') + ', ' + formData.created_at.split(' ')[1]
                                                            : new Date(formData.created_at).toLocaleString('pt-BR')
                                                    ) : 'Ainda não cadastrado'}
                                                </div>
                                            </div>
                                            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider">Última Alteração</label>
                                                <div className="text-sm font-semibold text-gray-600 mt-1 flex items-center gap-2">
                                                    <Edit size={14} /> 
                                                    {formData.updated_at ? (
                                                        formData.updated_at.includes('-')
                                                            ? formData.updated_at.split(' ')[0].split('-').reverse().join('/') + ', ' + formData.updated_at.split(' ')[1]
                                                            : new Date(formData.updated_at).toLocaleString('pt-BR')
                                                    ) : 'Ainda não alterado'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'variacoes' && (
                                    <div className="space-y-4 animate-in fade-in duration-200">
                                        <div className="flex justify-between items-center">
                                            <h4 className="text-sm font-bold text-gray-600 uppercase tracking-wider">Sabores / Tipos / Estoque Mínimo</h4>
                                            <button type="button" onClick={() => setFormData({...formData, variations: [...formData.variations, {description:'', code_ean:'', code_reduced:'', cost_price:0, sale_price:0, min_stock:0}]})} className="text-xs font-bold text-blue-600 flex items-center gap-1 hover:underline"><Plus size={14}/> Adicionar Variação</button>
                                        </div>
                                        <div className="space-y-3">
                                            {formData.variations.map((v, idx) => (
                                                <div key={idx} className="p-4 border rounded-lg bg-gray-50 grid grid-cols-1 md:grid-cols-7 gap-3 relative group">
                                                    <div className="md:col-span-1"><label className="text-[10px] font-bold text-gray-500 uppercase">DESCRIÇÃO</label><input type="text" required value={v.description} onChange={(e) => handleVariationChange(idx, 'description', e.target.value)} className="w-full p-1.5 text-xs border rounded mt-0.5" /></div>
                                                    <div><label className="text-[10px] font-bold text-gray-500 uppercase">EAN</label><input type="text" value={v.code_ean} onChange={(e) => handleVariationChange(idx, 'code_ean', e.target.value)} className="w-full p-1.5 text-xs border rounded mt-0.5" /></div>
                                                    <div><label className="text-[10px] font-bold text-gray-500 uppercase">RED</label><input type="text" value={v.code_reduced} onChange={(e) => handleVariationChange(idx, 'code_reduced', e.target.value)} className="w-full p-1.5 text-xs border rounded mt-0.5" /></div>
                                                    <div><label className="text-[10px] font-bold text-gray-500 uppercase">Preço Custo</label><input type="text" value={formatCurrencyInput(v.cost_price)} onChange={(e) => handleVariationChange(idx, 'cost_price', e.target.value)} className="w-full p-1.5 text-xs border rounded mt-0.5 font-bold text-right bg-white" /></div>
                                                    <div><label className="text-[10px] font-bold text-gray-500 uppercase">Preço Venda</label><input type="text" value={formatCurrencyInput(v.sale_price)} onChange={(e) => handleVariationChange(idx, 'sale_price', e.target.value)} className="w-full p-1.5 text-xs border rounded mt-0.5 font-bold text-right text-blue-600 bg-white" /></div>
                                                    <div className="md:col-span-2"><label className="text-[10px] font-bold text-orange-600 uppercase">Estoque Mín.</label><input type="number" step="0.01" value={v.min_stock} onChange={(e) => handleVariationChange(idx, 'min_stock', parseFloat(e.target.value))} className="w-full p-1.5 text-xs border border-orange-200 bg-orange-50 rounded mt-0.5 font-bold text-orange-700" /></div>
                                                    <button type="button" onClick={() => setFormData({...formData, variations: formData.variations.filter((_,i)=>i!==idx)})} className="absolute -right-2 -top-2 bg-red-100 text-red-600 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14}/></button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'fornecedores' && (
                                    <div className="space-y-4 animate-in fade-in duration-200">
                                        <div className="flex justify-between items-center">
                                            <h4 className="text-sm font-bold text-gray-600 uppercase tracking-wider">Vínculo de Fornecedores e Embalagens</h4>
                                            <button type="button" onClick={() => setFormData({...formData, packagings: [...formData.packagings, { supplier_id: '', packaging_type: 'un', conversion_factor: 1 }]})} className="text-xs font-bold text-blue-600 flex items-center gap-1 hover:underline"><Plus size={14}/> Adicionar Vínculo</button>
                                        </div>
                                        <div className="space-y-3">
                                            {formData.packagings.map((p, idx) => {
                                                const selectedSupplier = suppliers.find(s => s.id === p.supplier_id);
                                                return (
                                                    <div key={idx} className="p-4 border rounded-lg bg-gray-50 grid grid-cols-1 md:grid-cols-4 gap-4 relative group">
                                                        <div className="md:col-span-2 relative">
                                                            <label className="text-[10px] font-bold text-gray-500 uppercase text-blue-600">Buscar Fornecedor (Nome/Doc)</label>
                                                            <div className="relative mt-0.5">
                                                                <input 
                                                                    type="text"
                                                                    className="w-full p-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                                                    placeholder="Nome, CPF ou CNPJ..."
                                                                    value={supplierSearchIndices[idx] || (selectedSupplier ? selectedSupplier.name : '')}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value;
                                                                        setSupplierSearchIndices({...supplierSearchIndices, [idx]: val});
                                                                        if (!val) handlePackagingChange(idx, 'supplier_id', '');
                                                                    }}
                                                                    onFocus={() => setSupplierSearchIndices({...supplierSearchIndices, [idx]: supplierSearchIndices[idx] || ''})}
                                                                />
                                                                {supplierSearchIndices[idx] !== undefined && (
                                                                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-40 overflow-y-auto">
                                                                        {filterSuppliers(supplierSearchIndices[idx]).map(s => (
                                                                            <button
                                                                                key={s.id}
                                                                                type="button"
                                                                                className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 flex items-center justify-between border-b last:border-0"
                                                                                onClick={() => {
                                                                                    handlePackagingChange(idx, 'supplier_id', s.id);
                                                                                    const newIndices = {...supplierSearchIndices};
                                                                                    delete newIndices[idx];
                                                                                    setSupplierSearchIndices(newIndices);
                                                                                }}
                                                                            >
                                                                                <span className="font-bold truncate mr-2">{s.name}</span>
                                                                                <span className="text-[10px] text-gray-400 font-mono flex-shrink-0">{s.document_id}</span>
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div><label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Embalagem</label>
                                                            <select value={p.packaging_type} onChange={(e) => handlePackagingChange(idx, 'packaging_type', e.target.value)} className="w-full p-1.5 text-sm border rounded mt-0.5">
                                                                <option value="un">UN</option>
                                                                <option value="cx">CX</option>
                                                                <option value="kg">KG</option>
                                                                <option value="fd">FD</option>
                                                                <option value="sch">SCH</option>
                                                                <option value="sc">SC</option>
                                                                <option value="dp">DP</option>
                                                                <option value="eb">EB</option>
                                                                <option value="ml">ML</option>
                                                            </select>
                                                        </div>
                                                        <div><label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Fator</label><input type="number" min="1" value={p.conversion_factor} onChange={(e) => handlePackagingChange(idx, 'conversion_factor', parseInt(e.target.value))} className="w-full p-1.5 text-sm border rounded mt-0.5 text-center" /></div>
                                                        <button type="button" onClick={() => setFormData({...formData, packagings: formData.packagings.filter((_,i)=>i!==idx)})} className="absolute -right-2 -top-2 bg-red-100 text-red-600 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14}/></button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
                                <button type="button" onClick={handleCloseModal} className="px-4 py-2 text-gray-700 font-semibold hover:bg-gray-200 rounded-lg transition-colors">Cancelar</button>
                                <button type="submit" disabled={submitting} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 shadow-md transition-all flex items-center gap-2 disabled:bg-blue-400">
                                    {submitting ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Salvar Produto Completo
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductsPage;